import React, { useEffect, useState } from "react";
import {
  Stethoscope,
  Activity,
  Thermometer,
  HeartPulse,
  Camera,
  Volume2,
  Clock,
  ChevronDown,
  CalendarClock,
  FileText,
  MinusCircle,
  Droplets,
  ClipboardPlus,
  AudioLines,
  ImageIcon,
  FlaskConical,
  ZoomIn,
  X,
} from "lucide-react";
import { useMedWandSummary } from "../../../hooks/useMedWand";
import {
  MOCK_GROUP_READINGS,
  MOCK_CAPTURED_AT,
  MOCK_EXAM_META,
  groupForSensor,
} from "./medWandMockData";
import "./MedWandSection.css";

// A MedWand exam mixes three kinds of reading in one array: numbers, captured
// media, and readings a clinician deliberately skipped. Each needs its own
// treatment — a base64 image rendered as text is unreadable, and a skipped
// reading shown as "no data" loses the clinician's reason for skipping it.

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${fmtDate(iso)}, ${d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

// MedWand sends sensor names in PascalCase — "BloodPressure", "SpO2",
// "SpirometerFev1". Split on case boundaries but keep runs of capitals and
// digits together so SpO2 does not become "Sp O 2".
const humanSensor = (name) =>
  String(name || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .trim();

// Vitals worth surfacing above the fold, in the order a clinician reads them.
const HEADLINE_SENSORS = [
  "BloodPressure",
  "PulseRate",
  "SpO2",
  "Thermometer",
  "Respiratory",
  "Glucometer",
];

const iconForSensor = (sensor) => {
  if (/otoscope|dermatoscope/i.test(sensor)) return Camera;
  if (/stethoscope/i.test(sensor)) return Volume2;
  if (/thermo/i.test(sensor)) return Thermometer;
  if (/pulse|ecg|heart/i.test(sensor)) return HeartPulse;
  return Activity;
};

// The six panels MedWand's own device screen divides an exam into: the four
// named instruments, everything else, and the summary. Order matches the
// device, so a clinician moving between the two is not re-learning a layout.
const DEVICE_TABS = [
  { id: "sensors", label: "Sensors", Icon: Activity, accent: "indigo" },
  { id: "thermometer", label: "Thermometer", Icon: Thermometer, accent: "amber" },
  { id: "oximeter", label: "Pulse Oximeter", Icon: Droplets, accent: "cyan" },
  { id: "stethoscope", label: "Stethoscope", Icon: Stethoscope, accent: "violet" },
  { id: "camera", label: "Camera", Icon: Camera, accent: "pink" },
  { id: "summary", label: "Summary", Icon: ClipboardPlus, accent: "teal" },
];

// Readings for one tab. Live data always wins; the sample exam fills in only
// for the pilot client, so every other client sees the not-linked message
// rather than a sandbox patient's vitals. Returning `sample` rather than
// silently substituting is what lets the panel badge the difference.
function readingsForGroup(latestReadings, groupId, allowSample) {
  const live = (latestReadings || []).filter((r) => groupForSensor(r.sensor) === groupId);
  if (live.length) return { rows: live, sample: false };
  if (!allowSample) return { rows: [], sample: false };
  return { rows: MOCK_GROUP_READINGS[groupId] || [], sample: true };
}

function SampleBadge() {
  return (
    <div className="medwand-sample-badge">
      <FlaskConical size={13} />
      <span>
        <strong>Sample data</strong> — a MedWand sandbox exam, not this client&apos;s
        readings. Replaced automatically once their device reports this sensor group.
      </span>
    </div>
  );
}

// A numeric or free-text reading, laid out as a tile rather than a row: the
// device tabs are read at a glance, unlike the exam history's dense list.
function ValueTile({ reading }) {
  const Icon = iconForSensor(reading.sensor);
  return (
    <div className="medwand-tile">
      <span className="medwand-tile-icon">
        <Icon size={15} />
      </span>
      <span className="medwand-tile-label">
        {humanSensor(reading.sensor)}
        {reading.sensorMode && reading.sensorMode !== "Default" ? (
          <em> · {reading.sensorMode}</em>
        ) : null}
      </span>
      <span className="medwand-tile-value">
        {reading.raw || "—"}
        {reading.unit ? <em>{reading.unit}</em> : null}
      </span>
    </div>
  );
}

// Media captures. The sample set carries no blob — the backend fixture keeps
// its base64 truncated — so a capture with no `raw` renders as a labelled
// placeholder instead of a broken <img>.
function CaptureTile({ reading, onZoom }) {
  const isAudio = /^audio\//.test(reading.mediaType || "");
  const label = humanSensor(reading.sensorMode || reading.sensor);
  const site = reading.location ? humanSensor(reading.location) : "";

  if (reading.skipped) {
    return (
      <div className="medwand-capture skipped">
        <span className="medwand-capture-frame">
          <MinusCircle size={18} />
        </span>
        <span className="medwand-capture-label">{label}</span>
        <span className="medwand-capture-note">
          Not taken{site ? ` · ${site}` : ""}
          {reading.skipReason ? ` — ${reading.skipReason}` : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="medwand-capture">
      {reading.raw && !isAudio ? (
        <button
          type="button"
          className="medwand-capture-zoom"
          onClick={() => onZoom?.(reading)}
          aria-label={`Enlarge ${label}${site ? ` ${site}` : ""} capture`}
        >
          <img className="medwand-capture-img" src={reading.raw} alt={`${label} capture`} />
          <span className="medwand-capture-zoomhint">
            <ZoomIn size={14} />
          </span>
        </button>
      ) : reading.raw && isAudio ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio className="medwand-capture-audio" controls src={reading.raw} />
      ) : (
        <span className="medwand-capture-frame">
          {isAudio ? <AudioLines size={18} /> : <ImageIcon size={18} />}
        </span>
      )}
      <span className="medwand-capture-label">
        {label}
        {site ? <em> · {site}</em> : null}
      </span>
      <span className="medwand-capture-note">
        {reading.description ||
          `${site || (isAudio ? "Recording" : "Capture")}${
            reading.raw ? "" : " · preview unavailable"
          }`}
      </span>
    </div>
  );
}

// Full-size view of one capture. A 150px thumbnail is enough to see that an
// image exists, not enough to look at a lesion — so the tile opens the capture
// at whatever size the viewport allows.
function CaptureLightbox({ reading, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll under the overlay.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  if (!reading) return null;

  const label = humanSensor(reading.sensorMode || reading.sensor);
  const site = reading.location ? humanSensor(reading.location) : "";

  return (
    <div
      className="medwand-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${label} capture`}
      onClick={onClose}
    >
      {/* Stop propagation so a click on the image itself does not dismiss. */}
      <div className="medwand-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="medwand-lightbox-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <img src={reading.raw} alt={`${label} capture`} />
        <div className="medwand-lightbox-caption">
          <strong>
            {label}
            {site ? ` · ${site}` : ""}
          </strong>
          {reading.description ? <span>{reading.description}</span> : null}
        </div>
      </div>
    </div>
  );
}

function DevicePanel({ groupId, latestReadings, capturedAt, allowSample, accent }) {
  const { rows, sample } = readingsForGroup(latestReadings, groupId, allowSample);
  const [zoomed, setZoomed] = useState(null);

  if (!rows.length) {
    return (
      <div className="medwand-state empty">
        <Stethoscope size={22} />
        <p>No MedWand ID linked to this client</p>
        <span>
          Readings appear here once this client is linked to a MedWand device and an
          exam is completed on it.
        </span>
      </div>
    );
  }

  const media = rows.filter((r) => r.isMedia || r.skipped);
  const values = rows.filter((r) => !r.isMedia && !r.skipped);
  const when = sample ? MOCK_CAPTURED_AT : capturedAt;

  return (
    <div className={`medwand-device-panel medwand-accent-${accent}`}>
      {sample ? <SampleBadge /> : null}

      <div className="medwand-device-when">
        <Clock size={12} />
        <span>
          Recent exam data · {fmtDateTime(when)}
          {sample && MOCK_EXAM_META.examType ? ` · ${MOCK_EXAM_META.examType}` : ""}
        </span>
      </div>

      {values.length ? (
        <div className="medwand-tiles">
          {values.map((r, i) => (
            <ValueTile key={`${r.sensor}-${i}`} reading={r} />
          ))}
        </div>
      ) : null}

      {zoomed ? (
        <CaptureLightbox reading={zoomed} onClose={() => setZoomed(null)} />
      ) : null}

      {media.length ? (
        <div className="medwand-captures">
          {media.map((r, i) => (
            <CaptureTile
              key={`${r.sensor}-${r.sensorMode || ""}-${i}`}
              reading={r}
              onZoom={setZoomed}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Reading({ reading }) {
  const Icon = iconForSensor(reading.sensor);
  const label = humanSensor(reading.sensor);
  const mode =
    reading.sensorMode && reading.sensorMode !== "Default" ? reading.sensorMode : "";

  if (reading.skipped) {
    return (
      <div className="medwand-reading skipped">
        <span className="medwand-reading-icon">
          <MinusCircle size={14} />
        </span>
        <div className="medwand-reading-body">
          <span className="medwand-reading-label">
            {label}
            {mode ? <em> · {mode}</em> : null}
          </span>
          <span className="medwand-reading-skip">
            Not taken{reading.location ? ` · ${humanSensor(reading.location)}` : ""}
            {reading.skipReason ? ` — ${reading.skipReason}` : ""}
          </span>
        </div>
      </div>
    );
  }

  if (reading.isMedia) {
    const isImage = /^image\//.test(reading.mediaType);
    const isAudio = /^audio\//.test(reading.mediaType);
    return (
      <div className="medwand-reading media">
        <span className="medwand-reading-icon">
          <Icon size={14} />
        </span>
        <div className="medwand-reading-body">
          <span className="medwand-reading-label">
            {label}
            {mode ? <em> · {mode}</em> : null}
            {reading.location ? <em> · {humanSensor(reading.location)}</em> : null}
          </span>
          {isImage ? (
            <img className="medwand-media-image" src={reading.raw} alt={`${label} capture`} />
          ) : isAudio ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio className="medwand-media-audio" controls src={reading.raw} />
          ) : (
            <span className="medwand-reading-skip">
              {reading.mediaType || "media"} — no preview available
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="medwand-reading">
      <span className="medwand-reading-icon">
        <Icon size={14} />
      </span>
      <div className="medwand-reading-body">
        <span className="medwand-reading-label">
          {label}
          {mode ? <em> · {mode}</em> : null}
        </span>
        <span className="medwand-reading-value">
          {reading.raw || "—"}
          {/* The unit lives in sensorSettings, not in the reading: 97.2 is
              normal in Fahrenheit and fatal in Celsius. */}
          {reading.unit ? <span className="medwand-reading-unit">{reading.unit}</span> : null}
        </span>
      </div>
    </div>
  );
}

function ExamCard({ exam, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const mediaCount = (exam.readings || []).filter((r) => r.isMedia).length;

  return (
    <div className={`medwand-exam ${open ? "expanded" : ""}`}>
      <button
        type="button"
        className="medwand-exam-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="medwand-exam-head-left">
          <span className="medwand-exam-type">{exam.examType || "Exam"}</span>
          <span className="medwand-exam-when">{fmtDateTime(exam.startedAt)}</span>
        </div>
        <div className="medwand-exam-head-right">
          <span className="medwand-exam-meta">
            {exam.readingCount} readings
            {mediaCount ? ` · ${mediaCount} media` : ""}
            {exam.durationMinutes ? ` · ${exam.durationMinutes} min` : ""}
          </span>
          <ChevronDown className={`medwand-chevron ${open ? "open" : ""}`} size={16} />
        </div>
      </button>

      {open && (
        <div className="medwand-exam-body">
          {exam.doctorName ? (
            <div className="medwand-exam-clinician">Clinician: {exam.doctorName}</div>
          ) : null}

          {exam.practitionerNotes ? (
            <div className="medwand-notes">
              <span className="medwand-notes-label">
                <FileText size={13} /> Practitioner notes
              </span>
              <p>{exam.practitionerNotes}</p>
            </div>
          ) : null}

          {exam.patientNotes ? (
            <div className="medwand-notes">
              <span className="medwand-notes-label">
                <FileText size={13} /> Patient notes
              </span>
              <p>{exam.patientNotes}</p>
            </div>
          ) : null}

          <div className="medwand-readings">
            {(exam.readings || []).map((r, i) => (
              <Reading key={`${r.sensor}-${r.sortOrder}-${i}`} reading={r} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MedWandSection({ clientId, showSampleData = false }) {
  const { data, isLoading, isError, error } = useMedWandSummary(clientId);
  // Opens on the first tab, Sensors, so the panel lands on readings rather than
  // on Summary — which is empty for every client whose device has not reported.
  const [tab, setTab] = useState(DEVICE_TABS[0].id);

  if (isLoading) {
    return <div className="medwand-state">Loading MedWand exams…</div>;
  }

  if (isError) {
    return (
      <div className="medwand-state error">
        Could not load MedWand data
        {error?.response?.data?.message ? ` — ${error.response.data.message}` : ""}.
      </div>
    );
  }

  // No appointment has ever been created for this client. Nothing has gone
  // wrong, so the panel still renders: Summary says so plainly, and the device
  // tabs stay reachable showing the sample exam, which is what makes the layout
  // reviewable before a real device reports.
  const linked = !!data && data.linked !== false;

  const headline = HEADLINE_SENSORS.map((s) =>
    (data?.latestReadings || []).find((r) => r.sensor === s),
  ).filter(Boolean);

  return (
    <div className="medwand-wrap">
      <Header />

      <div className="medwand-devicetabs" role="tablist">
        {DEVICE_TABS.map(({ id, label, Icon, accent }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`medwand-devicetab medwand-accent-${accent} ${
              tab === id ? "active" : ""
            }`}
            onClick={() => setTab(id)}
          >
            <span className="medwand-devicetab-icon">
              <Icon size={26} strokeWidth={1.6} />
            </span>
            <span className="medwand-devicetab-label">{label}</span>
          </button>
        ))}
      </div>

      {tab !== "summary" ? (
        <DevicePanel
          accent={DEVICE_TABS.find((t) => t.id === tab)?.accent}
          groupId={tab}
          latestReadings={data?.latestReadings}
          capturedAt={data?.lastExamAt}
          allowSample={showSampleData}
        />
      ) : (
      <>
      {!linked ? (
        <div className="medwand-state empty">
          <Stethoscope size={22} />
          <p>No MedWand exams for this client yet.</p>
          <span>
            Exams appear here automatically once an appointment is created and completed
            on a MedWand device.
          </span>
          {showSampleData ? (
            <button
              type="button"
              className="medwand-sample-link"
              onClick={() => setTab("sensors")}
            >
              <FlaskConical size={13} /> View sample exam
            </button>
          ) : null}
        </div>
      ) : (
      <>
      <div className="medwand-summary">
        <div className="medwand-stat">
          <span className="medwand-stat-num">{data.totalExams}</span>
          <span className="medwand-stat-label">Exams</span>
        </div>
        <div className="medwand-stat">
          <span className="medwand-stat-num">{fmtDate(data.lastExamAt)}</span>
          <span className="medwand-stat-label">Last exam</span>
        </div>
        <div className="medwand-stat">
          <span className="medwand-stat-num">{(data.latestReadings || []).length}</span>
          <span className="medwand-stat-label">Sensors recorded</span>
        </div>
      </div>

      {data.upcoming?.length ? (
        <div className="medwand-upcoming">
          <CalendarClock size={14} />
          <span>
            Next scheduled: {fmtDateTime(data.upcoming[0].scheduledStartAt)}
            {data.upcoming[0].examType ? ` · ${data.upcoming[0].examType}` : ""}
          </span>
        </div>
      ) : null}

      {headline.length ? (
        <>
          <h3 className="medwand-heading">Latest vitals</h3>
          <div className="medwand-vitals">
            {headline.map((r) => {
              const Icon = iconForSensor(r.sensor);
              return (
                <div key={r.sensor} className="medwand-vital">
                  <span className="medwand-vital-icon">
                    <Icon size={15} />
                  </span>
                  <span className="medwand-vital-label">{humanSensor(r.sensor)}</span>
                  <span className="medwand-vital-value">
                    {r.raw}
                    {r.unit ? <em>{r.unit}</em> : null}
                  </span>
                  <span className="medwand-vital-when">
                    <Clock size={11} /> {fmtDate(r.measuredAt)}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <h3 className="medwand-heading">Exam history</h3>
      <div className="medwand-exams">
        {(data.exams || []).map((exam, i) => (
          <ExamCard key={exam.appointmentId} exam={exam} defaultOpen={i === 0} />
        ))}
      </div>
      </>
      )}
      </>
      )}
    </div>
  );
}

function Header() {
  return (
    <div className="medwand-header">
      <div className="medwand-brand-icon">
        <Stethoscope size={18} />
      </div>
      <div>
        <h2 className="medwand-title">MedWand</h2>
        <p className="medwand-subtitle">
          Vitals, imaging and auscultation captured on the client's MedWand device.
        </p>
      </div>
    </div>
  );
}
